import type { OperationsFleetDevice } from '@/types/operations';

/** Most devices the search sheet lists at once; typing more narrows the rest. */
export const MAX_DEVICE_SEARCH_RESULTS = 8;

export interface DeviceSearchResult {
  matches: OperationsFleetDevice[];
  /** All devices that match, including the ones not listed. */
  total: number;
}

const DEVICE_ID_QUERY = /^#?(\d+)$/;

/** Exact ID first, then names that start with the query, then names that contain it. */
function matchRank(record: OperationsFleetDevice, query: string): number | null {
  const idMatch = DEVICE_ID_QUERY.exec(query);
  if (idMatch && record.device.device_id === Number(idMatch[1])) return 0;
  const name = record.device.device_name.toLocaleLowerCase();
  if (name.startsWith(query)) return 1;
  if (name.includes(query)) return 2;
  return null;
}

/** Devices whose name contains the query, or whose ID is the query ("12" or "#12"). */
export function searchFleetDevices(
  records: readonly OperationsFleetDevice[],
  query: string,
  limit: number = MAX_DEVICE_SEARCH_RESULTS,
): DeviceSearchResult {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return { matches: [], total: 0 };

  const ranked = records
    .map((record) => ({ record, rank: matchRank(record, normalized) }))
    .filter((entry): entry is { record: OperationsFleetDevice; rank: number } => entry.rank !== null)
    .sort(
      (left, right) =>
        left.rank - right.rank ||
        left.record.device.device_name.localeCompare(right.record.device.device_name),
    );
  return {
    matches: ranked.slice(0, limit).map((entry) => entry.record),
    total: ranked.length,
  };
}
